/**
 * Autenticação — os caminhos que o Traccar oferece.
 *
 *   POST /api/session                 e-mail + senha (+ código, com 2FA)
 *   GET  /api/session?token=…         entrada por token (link compartilhado)
 *   POST /api/password/reset          pede o e-mail de recuperação
 *   POST /api/password/update         troca a senha com o token do e-mail
 *   POST /api/users                   cadastro próprio, se o servidor permitir
 *   POST /api/users/totp              gera a chave de dois fatores
 *   POST /api/session/token           cria chave de acesso (integração)
 *   POST /api/session/token/revoke    revoga uma chave
 *   POST /api/share/device            link temporário de um veículo
 *
 * ⚠️ Esses endpoints falam `application/x-www-form-urlencoded`, não JSON, e
 * devolvem **texto puro** no erro — inclusive stack trace do Java. Em produção
 * o `/api/password/reset` sem SMTP responde 400 com
 * `jakarta.mail.MessagingException…`. Nada disso pode chegar à tela: o que o
 * cliente vê sai de `motivo()`.
 */
import { Unauthorized, type User } from "./traccar";

export interface ServidorInfo {
  registration: boolean;
  openIdEnabled: boolean;
  openIdForce: boolean;
  emailEnabled: boolean;     // false ⇒ não há como recuperar senha por e-mail
  announcement: string | null;
  version: string;
}

export type Entrada =
  | { estado: "ok"; user: User }
  | { estado: "2fa" }                       // o servidor pediu o código
  | { estado: "erro"; motivo: string };

const MOCK = import.meta.env.DEV && import.meta.env.VITE_MOCK !== "0";

const form = (dados: Record<string, string>): RequestInit => ({
  method: "POST",
  credentials: "same-origin",
  headers: { "content-type": "application/x-www-form-urlencoded" },
  body: new URLSearchParams(dados).toString(),
});

/** Erro do Traccar → frase. O texto cru é log, não interface. */
export function motivo(status: number, texto: string): string {
  const t = texto.toLowerCase();
  if (t.includes("no smtp configuration"))
    return "Este servidor ainda não tem envio de e-mail configurado, então a " +
           "recuperação automática não funciona. Peça a um administrador para " +
           "trocar sua senha em Cadastros › Usuários.";
  if (status === 401) return "E-mail ou senha não conferem.";
  if (status === 403) return "Seu usuário não tem permissão para isso.";
  if (t.includes("duplicate") || t.includes("unique")) return "Já existe uma conta com esse e-mail.";
  if (status === 400) return "Os dados enviados não foram aceitos.";
  if (status >= 500) return "O servidor falhou ao processar. Tente de novo em instantes.";
  return "Não foi possível concluir.";
}

export const auth = {
  async servidor(): Promise<ServidorInfo> {
    if (MOCK) return { registration: false, openIdEnabled: false, openIdForce: false,
                       emailEnabled: true, announcement: null, version: "6.15.3" };
    const r = await fetch("/api/server", { credentials: "same-origin" });
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
  },

  async entrar(email: string, senha: string, codigo?: string): Promise<Entrada> {
    if (MOCK) return { estado: "ok", user: { id: 1, name: "Operador", email,
                                             administrator: true } as User };
    const r = await fetch("/api/session",
      form(codigo ? { email, password: senha, code: codigo } : { email, password: senha }));
    if (r.ok) return { estado: "ok", user: await r.json() };
    // 401 + WWW-Authenticate: TOTP é pedido de segundo fator, não senha errada.
    if (r.status === 401 && (r.headers.get("WWW-Authenticate") ?? "").includes("TOTP"))
      return { estado: "2fa" };
    return { estado: "erro", motivo: motivo(r.status, await r.text().catch(() => "")) };
  },

  async entrarComToken(token: string): Promise<User> {
    const r = await fetch(`/api/session?token=${encodeURIComponent(token)}`,
                          { credentials: "same-origin" });
    if (!r.ok) throw new Unauthorized();
    return r.json();
  },

  async pedirRecuperacao(email: string): Promise<void> {
    if (MOCK) return;
    const r = await fetch("/api/password/reset", form({ email }));
    if (!r.ok) throw new Error(motivo(r.status, await r.text().catch(() => "")));
  },

  async definirSenha(token: string, senha: string): Promise<void> {
    if (MOCK) return;
    const r = await fetch("/api/password/update", form({ token, password: senha }));
    if (!r.ok) throw new Error(motivo(r.status, await r.text().catch(() => "")));
  },

  async registrar(name: string, email: string, password: string): Promise<void> {
    if (MOCK) return;
    const r = await fetch("/api/users", {
      method: "POST", credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name, email, password }),
    });
    if (!r.ok) throw new Error(motivo(r.status, await r.text().catch(() => "")));
  },

  async gerarTotp(): Promise<string> {
    if (MOCK) return "JBSWY3DPEHPK3PXP";
    const r = await fetch("/api/users/totp", { method: "POST", credentials: "same-origin" });
    if (!r.ok) throw new Error(motivo(r.status, await r.text().catch(() => "")));
    return (await r.text()).trim();
  },

  /** Chave de acesso para integração. Devolve o token em texto puro. */
  async gerarChave(expiracao: Date): Promise<string> {
    if (MOCK) return "MOCKTOKEN" + Math.random().toString(36).slice(2, 10);
    const r = await fetch("/api/session/token",
                          form({ expiration: expiracao.toISOString() }));
    if (!r.ok) throw new Error(motivo(r.status, await r.text().catch(() => "")));
    return (await r.text()).trim();
  },

  async revogarChave(token: string): Promise<void> {
    if (MOCK) return;
    const r = await fetch("/api/session/token/revoke", form({ token }));
    if (!r.ok) throw new Error(motivo(r.status, await r.text().catch(() => "")));
  },

  /** Link temporário de acompanhamento de um veículo. */
  async compartilhar(deviceId: number, expiracao: Date): Promise<string> {
    if (MOCK) return `${location.origin}?token=MOCKSHARE${deviceId}`;
    const r = await fetch("/api/share/device",
      form({ deviceId: String(deviceId), expiration: expiracao.toISOString() }));
    if (!r.ok) throw new Error(motivo(r.status, await r.text().catch(() => "")));
    return `${location.origin}?token=${encodeURIComponent((await r.text()).trim())}`;
  },

  entrarComOpenId() { location.href = "/api/session/openid/auth"; },
};

/** Chave TOTP → URI que os aplicativos de autenticação leem. */
export const uriTotp = (chave: string, email: string, marca: string) =>
  `otpauth://totp/${encodeURIComponent(marca)}:${encodeURIComponent(email)}` +
  `?secret=${chave}&issuer=${encodeURIComponent(marca)}`;
